import React, { useState, useRef, useEffect } from "react";
import { X } from "lucide-react";

interface ColorPickerWithPaletteProps {
    value: string;
    onChange: (color: string) => void;
    onClose?: () => void;
    label?: string;
    showAdvanced?: boolean;
}

// Predefined color palette (80 colors in 8 rows x 10 columns)
const COLOR_PALETTE = [
    // Row 1: Grays and whites
    "#000000", "#1C1C1C", "#383838", "#555555", "#717171", "#8D8D8D", "#AAAAAA", "#C6C6C6", "#E2E2E2", "#FFFFFF",
    // Row 2: Reds
    "#800000", "#8B0000", "#A52A2A", "#B22222", "#DC143C", "#FF0000", "#FF6347", "#FF7F50", "#FFA07A", "#FFB6C1",
    // Row 3: Oranges and Browns
    "#FF4500", "#FF8C00", "#FFA500", "#FFD700", "#DAA520", "#B8860B", "#CD853F", "#D2691E", "#A0522D", "#8B4513",
    // Row 4: Yellows and Greens
    "#FFFF00", "#FFFFE0", "#FFFACD", "#FFF8DC", "#ADFF2F", "#7FFF00", "#7CFC00", "#32CD32", "#228B22", "#006400",
    // Row 5: Greens and Cyans
    "#00FF00", "#00FA9A", "#00CED1", "#20B2AA", "#48D1CC", "#40E0D0", "#00FFFF", "#00BFFF", "#1E90FF", "#0000FF",
    // Row 6: Blues
    "#0000CD", "#00008B", "#191970", "#4169E1", "#6495ED", "#87CEEB", "#87CEFA", "#B0C4DE", "#4682B4", "#5F9EA0",
    // Row 7: Purples and Pinks
    "#8A2BE2", "#9400D3", "#9932CC", "#BA55D3", "#DA70D6", "#DDA0DD", "#EE82EE", "#FF00FF", "#FF1493", "#FF69B4",
    // Row 8: Mixed colors
    "#C71585", "#DB7093", "#FFC0CB", "#FFB6C1", "#FFA07A", "#FF7F50", "#FF6347", "#FF4500", "#FFD700", "#FFFF00",
];

const ColorPickerWithPalette: React.FC<ColorPickerWithPaletteProps> = ({
    value,
    onChange,
    onClose,
    label = "Shift Color (Optional)",
    showAdvanced = false,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedColor, setSelectedColor] = useState(value || "#744700");
    const [hue, setHue] = useState(0);
    const [saturation, setSaturation] = useState(100);
    const [lightness, setLightness] = useState(50);
    const [isInitialized, setIsInitialized] = useState(false);
    const pickerRef = useRef<HTMLDivElement>(null);

    // Convert HSL to HEX
    const hslToHex = (h: number, s: number, l: number): string => {
        l /= 100;
        const a = (s * Math.min(l, 1 - l)) / 100;
        const f = (n: number) => {
            const k = (n + h / 30) % 12;
            const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
            return Math.round(255 * color)
                .toString(16)
                .padStart(2, "0");
        };
        return `#${f(0)}${f(8)}${f(4)}`.toUpperCase();
    };

    // Convert HEX to HSL
    const hexToHsl = (hex: string): [number, number, number] => {
        const r = parseInt(hex.slice(1, 3), 16) / 255;
        const g = parseInt(hex.slice(3, 5), 16) / 255;
        const b = parseInt(hex.slice(5, 7), 16) / 255;

        const max = Math.max(r, g, b);
        const min = Math.min(r, g, b);
        let h = 0;
        let s = 0;
        const l = (max + min) / 2;

        if (max !== min) {
            const d = max - min;
            s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
            switch (max) {
                case r:
                    h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
                    break;
                case g:
                    h = ((b - r) / d + 2) / 6;
                    break;
                case b:
                    h = ((r - g) / d + 4) / 6;
                    break;
            }
        }

        return [Math.round(h * 360), Math.round(s * 100), Math.round(l * 100)];
    };

    // Initialize HSL from current color only once when opening
    useEffect(() => {
        if (isOpen && !isInitialized) {
            const currentColor = value || "#744700";
            const [h, s, l] = hexToHsl(currentColor);
            setHue(h);
            setSaturation(s);
            setLightness(l);
            setSelectedColor(currentColor);
            setIsInitialized(true);
        } else if (!isOpen) {
            setIsInitialized(false);
        }
    }, [isOpen, value, isInitialized]);

    // Update color when HSL changes (only after initialization)
    useEffect(() => {
        if (isInitialized) {
            const newColor = hslToHex(hue, saturation, lightness);
            if (newColor !== selectedColor) {
                setSelectedColor(newColor);
                onChange(newColor);
            }
        }
    }, [hue, saturation, lightness, isInitialized, selectedColor, onChange]);

    // Close picker when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
            return () => document.removeEventListener("mousedown", handleClickOutside);
        }
    }, [isOpen]);

    const handlePaletteClick = (color: string) => {
        const [h, s, l] = hexToHsl(color);
        setHue(h);
        setSaturation(s);
        setLightness(l);
        setSelectedColor(color);
        onChange(color);
        setIsOpen(false);
        if (onClose) onClose();
    };

    const handleGradientClick = (e: React.MouseEvent<HTMLDivElement>) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const x = Math.max(0, Math.min(rect.width, e.clientX - rect.left));
        const y = Math.max(0, Math.min(rect.height, e.clientY - rect.top));
        const s = Math.round((x / rect.width) * 100);
        const l = Math.round(100 - (y / rect.height) * 100);
        setSaturation(Math.max(0, Math.min(100, s)));
        setLightness(Math.max(0, Math.min(100, l)));
    };

    const handleHueSliderClick = (e: React.MouseEvent<HTMLDivElement>) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const y = Math.max(0, Math.min(rect.height, e.clientY - rect.top));
        const h = Math.round((y / rect.height) * 360);
        setHue(Math.max(0, Math.min(360, h)));
    };

    const handleCancel = () => {
        const originalColor = value || "#744700";
        const [h, s, l] = hexToHsl(originalColor);
        setHue(h);
        setSaturation(s);
        setLightness(l);
        setSelectedColor(originalColor);
        setIsOpen(false);
        setIsInitialized(false);
        if (onClose) onClose();
    };

    return (
        <div className="relative" ref={pickerRef}>
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
                <div className="flex items-center gap-2">
                    <div
                        className="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer"
                        style={{ backgroundColor: value || "#744700" }}
                        onClick={() => setIsOpen(!isOpen)}
                    />
                    <input
                        type="text"
                        value={value || "#744700"}
                        onChange={(e) => onChange(e.target.value)}
                        disabled
                        className="flex-1 h-10 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed"
                        placeholder="#744700"
                    />
                </div>
            </div>

            {isOpen && (
                <div 
                    className="absolute mt-2 bg-white rounded-lg shadow-xl border border-gray-200 p-3" 
                    style={{ 
                        width: showAdvanced ? "480px" : "280px",
                        right: 0,
                        top: "100%",
                        zIndex: 10000
                    }}
                >
                    <div className={showAdvanced ? "flex gap-3" : ""}>
                        {/* Color Palette */}
                        <div className={showAdvanced ? "flex-shrink-0" : ""}>
                            <div className="text-xs font-medium text-gray-700 mb-1.5">Color Palette</div>
                            <div className="grid grid-cols-10 gap-0.5 w-64">
                                {COLOR_PALETTE.map((color, index) => (
                                    <button
                                        key={index}
                                        type="button"
                                        className={`w-5 h-5 rounded border ${
                                            selectedColor.toUpperCase() === color.toUpperCase()
                                                ? "border-blue-500 ring-1 ring-blue-300"
                                                : "border-gray-300 hover:border-gray-400"
                                        }`}
                                        style={{ backgroundColor: color }}
                                        onClick={() => handlePaletteClick(color)}
                                        title={color}
                                    />
                                ))}
                            </div>
                        </div>

                        {/* Advanced Color Picker */}
                        {showAdvanced && (
                            <div className="flex-1">
                                <div className="text-xs font-medium text-gray-700 mb-1.5">Advanced</div>
                                <div className="flex gap-2">
                                    {/* Saturation/Lightness Gradient */}
                                    <div className="relative">
                                        <div
                                            className="w-36 h-36 rounded border border-gray-300 cursor-crosshair relative overflow-hidden"
                                            onClick={handleGradientClick}
                                        >
                                            {/* Saturation gradient (horizontal) */}
                                            <div
                                                className="absolute inset-0"
                                                style={{
                                                    background: `linear-gradient(to right, 
                                                        hsl(${hue}, 0%, ${lightness}%) 0%, 
                                                        hsl(${hue}, 100%, ${lightness}%) 100%)`,
                                                }}
                                            />
                                            {/* Lightness gradient (vertical) */}
                                            <div
                                                className="absolute inset-0"
                                                style={{
                                                    background: `linear-gradient(to bottom, 
                                                        transparent 0%, 
                                                        rgba(0, 0, 0, 1) 100%)`,
                                                }}
                                            />
                                            {/* White to transparent gradient (vertical) */}
                                            <div
                                                className="absolute inset-0"
                                                style={{
                                                    background: `linear-gradient(to top, 
                                                        transparent 0%, 
                                                        rgba(255, 255, 255, 1) 100%)`,
                                                }}
                                            />
                                            {/* Selection indicator */}
                                            <div
                                                className="absolute w-2.5 h-2.5 border-2 border-white rounded-full shadow-lg pointer-events-none"
                                                style={{
                                                    left: `${saturation}%`,
                                                    top: `${100 - lightness}%`,
                                                    transform: "translate(-50%, -50%)",
                                                }}
                                            />
                                        </div>
                                    </div>

                                    {/* Hue Slider */}
                                    <div className="relative">
                                        <div
                                            className="w-6 h-36 rounded border border-gray-300 cursor-pointer"
                                            style={{
                                                background: `linear-gradient(to bottom, 
                                                    #FF0000 0%, 
                                                    #FFFF00 16.66%, 
                                                    #00FF00 33.33%, 
                                                    #00FFFF 50%, 
                                                    #0000FF 66.66%, 
                                                    #FF00FF 83.33%, 
                                                    #FF0000 100%)`,
                                            }}
                                            onClick={handleHueSliderClick}
                                        >
                                            <div
                                                className="absolute left-0 right-0 w-2.5 h-2.5 border-2 border-white rounded-full shadow-lg"
                                                style={{
                                                    top: `${(hue / 360) * 100}%`,
                                                    transform: "translate(-50%, -50%)",
                                                    left: "50%",
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Selected Color Preview */}
                                <div className="mt-3 flex items-center gap-3">
                                    <div
                                        className="w-12 h-12 rounded border-2 border-gray-300"
                                        style={{ backgroundColor: selectedColor }}
                                    />
                                    <div className="flex-1">
                                        <div className="text-xs text-gray-600 mb-0.5">Selected Color</div>
                                        <div className="text-xs font-mono text-gray-900">{selectedColor}</div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Cancel Button */}
                    <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-gray-200">
                        <button
                            type="button"
                            onClick={handleCancel}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ColorPickerWithPalette;

